/**
 * The chat composer's "Plain language" toggle (D2, workspace docs/guardian/03
 * §3.3, 2026-10-22). The effective value is the user's own stored choice when
 * they have made one, otherwise the department's `plainLanguageDefault`. The
 * toggle is the authority the agent obeys, so the value is sent on every
 * message (see useChatTurn's buildChatPayload).
 *
 * The department default is read once per sign-in from GET /department/settings
 * (any member may read it). A 503 `settings_unavailable`/`terms_unavailable`,
 * no department, or any failure leaves the default off.
 */
import { useCallback, useEffect, useState } from 'react';
import { fetchDepartmentSettings } from '../api/guardianApi';
import { readPlainLanguage, writePlainLanguage } from '../utils/plainLanguage';

/**
 * @param {object} args
 * @param {string|null} args.userId
 * @param {string|null} args.authToken
 * @param {boolean} args.hasDepartment - The signed-in user belongs to a department.
 * @returns {{ plainLanguage: boolean, setPlainLanguage: (value: boolean) => void }}
 */
export function usePlainLanguage({ userId, authToken, hasDepartment }) {
  // null until the user sets it: fall back to the department default.
  const [stored, setStored] = useState(() => readPlainLanguage(userId));
  const [deptDefault, setDeptDefault] = useState(false);

  // Re-read this user's own choice whenever the signed-in user changes, so a
  // sign-out or account switch never shows the previous user's toggle.
  useEffect(() => {
    setStored(readPlainLanguage(userId));
  }, [userId]);

  useEffect(() => {
    let live = true;
    if (!authToken || !hasDepartment) {
      setDeptDefault(false);
      return () => { live = false; };
    }
    fetchDepartmentSettings(authToken).then((res) => {
      if (live) setDeptDefault(Boolean(res?.success && res.data?.plainLanguageDefault));
    });
    return () => { live = false; };
  }, [authToken, hasDepartment, userId]);

  const setPlainLanguage = useCallback((value) => {
    const next = Boolean(value);
    writePlainLanguage(userId, next);
    setStored(next);
  }, [userId]);

  return { plainLanguage: stored === null ? deptDefault : stored, setPlainLanguage };
}
